"use client";

import React from "react";
import ActionListSkeleton from "@/components/Common/ActionListSkeleton";
import { parseSettingsPanel } from "~/src/containers/account-setting/settings-navigation";
import { Skeleton } from "@/components/ui/adapters";

const LIST_PANELS = new Set([
  "blocked-list",
  "nickname-list",
  "d-bot-permission",
  "audience-subscribed",
]);

const LIST_PANELS_WITH_SEARCH = new Set(["blocked-list", "nickname-list"]);

/** Sold items & Revenue: ClaimCardList + History (transaction/sold list), not member list */
const REVENUE_SOLD_PANELS = new Set(["audience-revenue", "pay-to-open-sold"]);

/** Purchased items: date-grouped PTOPurchaseCard list only (no claim cards, no search) */
const PURCHASED_PANELS = new Set(["pay-to-open-purchased"]);

interface SettingsPageSkeletonProps {
  /** Current panel from URL (?panel=). Drives which main content skeleton is shown. */
  panel?: string | null;
}

/**
 * Skeleton for settings page while data is loading.
 * Matches AccountSettingContainer layout: sidebar (section cards) + main content panel.
 * Main content skeleton matches the selected panel (from URL) so the right layout is shown.
 */
export function SettingsPageSkeleton({
  panel,
}: SettingsPageSkeletonProps): React.ReactElement {
  const explicitPanel = parseSettingsPanel(
    new URLSearchParams({ panel: panel ?? "" }).toString()
  );
  const isRevenueSoldPanel = panel && REVENUE_SOLD_PANELS.has(panel);
  const isPurchasedPanel = panel && PURCHASED_PANELS.has(panel);
  const isListPanel = panel && LIST_PANELS.has(panel);
  const withSearch = panel && LIST_PANELS_WITH_SEARCH.has(panel);
  const isScrollPanel = [isRevenueSoldPanel, isPurchasedPanel].some(Boolean);

  return (
    <div
      aria-label="Loading settings"
      className="w-full min-h-[calc(100dvh-4.5rem-env(safe-area-inset-bottom))] bg-background-light xl:min-h-0"
      role="status"
    >
      {explicitPanel === null ? (
        <div
          aria-label="Loading settings menu"
          className="flex flex-col gap-2 px-4 py-4 md:px-8 xl:hidden"
        >
          {[1, 2, 3, 2, 2].map((rowCount, sectionIndex) => (
            <section
              className="overflow-hidden rounded-2xl border border-secondary-light bg-white"
              key={sectionIndex}
            >
              <div className="border-b border-secondary-light px-3 py-3">
                <Skeleton className="h-4 w-28 rounded-lg bg-secondary-light" />
              </div>
              <div className="flex flex-col gap-1 p-3">
                {Array.from({ length: rowCount }, (_, rowIndex) => (
                  <div
                    className="flex items-center gap-3 rounded-xl p-3"
                    key={rowIndex}
                  >
                    <Skeleton className="size-5 shrink-0 rounded bg-secondary-light" />
                    <Skeleton className="h-4 flex-1 rounded-md bg-secondary-light" />
                    <Skeleton className="size-5 shrink-0 rounded bg-secondary-light" />
                  </div>
                ))}
              </div>
            </section>
          ))}
          <div className="rounded-2xl border border-secondary-light bg-white p-3">
            <Skeleton className="h-8 w-full rounded-lg bg-secondary-light" />
          </div>
        </div>
      ) : (
        <div
          aria-label="Loading settings detail"
          className="px-4 py-4 md:px-8 xl:hidden"
        >
          <Skeleton className="mb-4 h-11 w-32 rounded-xl bg-secondary-light" />
          <div className="rounded-2xl border border-secondary-light bg-white p-4">
            <Skeleton className="mb-3 h-6 w-48 rounded-lg bg-secondary-light" />
            <Skeleton className="mb-6 h-4 w-full rounded-lg bg-secondary-light" />
            {withSearch ? (
              <Skeleton className="mb-4 h-11 w-full rounded-xl bg-secondary-light" />
            ) : null}
            {Array.from({ length: isListPanel ? 5 : 2 }, (_, index) => (
              <Skeleton
                key={index}
                className="mb-4 h-16 w-full rounded-xl bg-secondary-light"
              />
            ))}
          </div>
        </div>
      )}
      <div className="hidden w-full justify-center items-start h-[calc(100vh-90px)] pt-6 overflow-y-hidden xl:flex">
        <div className="w-full h-full max-w-7xl mx-auto px-8">
          <div className="grid w-full h-full gap-6 grid-cols-12 items-start">
            {/* Sidebar: section cards skeleton */}
            <aside className="col-span-4 flex flex-col h-full overflow-y-auto no-scrollbar">
              <nav className="flex flex-col pb-4 w-full gap-2">
                {[1, 2, 3, 4].map((sectionIndex) => (
                  <div
                    key={sectionIndex}
                    className="bg-white border border-secondary-light rounded-2xl overflow-hidden"
                  >
                    <div className="px-3 py-2 border-b border-secondary-light">
                      <Skeleton className="h-4 w-28 rounded-lg bg-secondary-light" />
                    </div>
                    <div className="flex flex-col gap-1 p-3">
                      {[1, 2, 3].map((rowIndex) => (
                        <div
                          key={rowIndex}
                          className="flex items-center gap-3 p-3 rounded-xl"
                        >
                          <Skeleton className="size-5 rounded shrink-0 bg-secondary-light" />
                          <Skeleton className="h-4 flex-1 rounded-md bg-secondary-light" />
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </nav>
            </aside>

            {/* Main content: layout matches selected panel (Security form rows vs list with search) */}
            <main className="col-span-8 flex flex-1 w-full h-[calc(100vh-120px)]">
              <div
                className={`w-full h-full border border-secondary-light rounded-2xl bg-white ${isScrollPanel ? "py-6 px-0" : "p-6"}`}
              >
                <div className="flex flex-col h-full">
                  <div
                    className={`flex items-start justify-between gap-4 mb-4 ${isScrollPanel ? "px-4" : ""}`}
                  >
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <Skeleton className="size-5 rounded shrink-0 bg-secondary-light" />
                        <Skeleton className="h-7 w-40 rounded bg-secondary-light" />
                      </div>
                      <Skeleton className="h-5 w-full max-w-2xl rounded bg-secondary-light" />
                    </div>
                  </div>
                  <div
                    className={`space-y-0.5 border-t border-gray-100 flex flex-col flex-1 min-h-0 overflow-y-auto ${[isListPanel, isScrollPanel].some(Boolean) ? "pt-4" : "pt-6"}`}
                  >
                    {isRevenueSoldPanel ? (
                      /* Revenue / Sold: same structure as SettingSection > ScrollArea(px-4) > flex flex-col gap-4 > ClaimCardList + TransactionHistory */
                      <div className="w-full px-4 flex flex-col gap-4 flex-1 min-h-0">
                        {/* ClaimCardList: carousel row, same as ClaimCarousel */}
                        <section className="w-full relative shrink-0">
                          <div className="overflow-hidden">
                            <div className="flex gap-2 -ml-1">
                              {[1, 2].map((i) => (
                                <div
                                  key={i}
                                  className="transform-gpu flex-none min-w-fit px-1"
                                >
                                  {/* ClaimCard: article w-[350px] border rounded-2xl border-secondary-light */}
                                  <article className="flex flex-col items-start border rounded-2xl border-secondary-light w-[350px] cursor-default select-none">
                                    {/* Top: NetworkBackground → bg-gray-100 rounded-2xl p-4 gap-4 */}
                                    <div className="flex overflow-hidden relative flex-col gap-4 items-start p-4 bg-gray-100 rounded-2xl w-full">
                                      <div className="flex flex-col gap-2 w-full pb-4">
                                        {/* ClaimHeader */}
                                        <header className="flex relative gap-2 items-center w-full justify-between">
                                          <Skeleton className="h-5 w-16 rounded-full bg-secondary-light" />
                                          <div className="flex relative gap-1 justify-end items-center">
                                            <Skeleton className="size-5 rounded-full bg-secondary-light" />
                                            <Skeleton className="h-5 w-24 rounded-full bg-secondary-light" />
                                          </div>
                                        </header>
                                        {/* Total USD (app-text-h3) */}
                                        <div className="flex gap-2 w-full items-center">
                                          <Skeleton className="h-8 w-24 rounded-full bg-secondary-light" />
                                        </div>
                                        {/* ClaimAmount: 2 token lines */}
                                        <div className="flex flex-col gap-2 w-full">
                                          <Skeleton className="h-6 w-full rounded-full bg-secondary-light" />
                                          <Skeleton className="h-6 w-4/5 rounded-full bg-secondary-light" />
                                        </div>
                                      </div>
                                      {/* ClaimButton */}
                                      <Skeleton className="h-10 w-full rounded-xl bg-secondary-light" />
                                    </div>
                                    {/* Bottom: total_sold + TotalClaimed */}
                                    <div className="w-full flex flex-col gap-2 p-4">
                                      <Skeleton className="h-4 w-16 rounded-full bg-secondary-light" />
                                      <div className="flex gap-1 items-center">
                                        <Skeleton className="size-5 rounded-full bg-secondary-light" />
                                        <Skeleton className="h-5 w-20 rounded-full bg-secondary-light" />
                                      </div>
                                    </div>
                                  </article>
                                </div>
                              ))}
                            </div>
                          </div>
                        </section>
                        {/* TransactionHistory: main flex flex-col w-full gap-2, Body1 py-2, then date groups */}
                        <main className="flex flex-col w-full gap-2 flex-1 min-h-0">
                          <div className="flex-1 shrink-0 py-2">
                            <Skeleton className="h-5 w-16 rounded-full bg-secondary-light" />
                          </div>
                          <div className="w-full flex flex-col overflow-y-auto">
                            {[1, 2].map((groupIdx) => (
                              <div key={groupIdx} className="w-full">
                                <div className="py-4">
                                  <Skeleton className="h-4 w-20 rounded-full bg-secondary-light" />
                                </div>
                                <div className="flex flex-col gap-4">
                                  {[1, 2].map((cardIdx) => (
                                    <div
                                      key={cardIdx}
                                      className="overflow-hidden p-4 w-full bg-white rounded-2xl border border-gray-100 flex flex-col gap-2"
                                    >
                                      <div className="flex flex-wrap justify-between items-start w-full">
                                        <Skeleton className="h-5 w-16 rounded-full bg-secondary-light" />
                                        <div className="flex gap-1 items-center">
                                          <Skeleton className="h-5 w-16 rounded-full bg-secondary-light" />
                                          <Skeleton className="size-4 rounded-full bg-secondary-light" />
                                          <Skeleton className="h-4 w-8 rounded-full bg-secondary-light" />
                                        </div>
                                      </div>
                                      <Skeleton className="h-5 w-full rounded-full bg-secondary-light" />
                                      <div className="flex gap-2 items-center mt-2 max-w-full">
                                        <Skeleton className="size-6 rounded-full bg-secondary-light" />
                                        <div className="flex flex-1 gap-1 items-center">
                                          <Skeleton className="h-4 w-20 rounded-full bg-secondary-light" />
                                          <Skeleton className="h-4 w-16 rounded-full bg-secondary-light" />
                                          <Skeleton className="size-4 rounded-full bg-secondary-light" />
                                        </div>
                                      </div>
                                      <div className="w-full py-2">
                                        <div className="h-px w-full border-t border-dashed border-secondary-light" />
                                      </div>
                                      <div className="flex flex-wrap gap-2 items-center justify-between w-full">
                                        <Skeleton className="h-4 w-8 rounded-full bg-secondary-light" />
                                        <div className="flex gap-1 items-center">
                                          <Skeleton className="size-6 rounded-full bg-secondary-light" />
                                          <Skeleton className="h-4 w-24 rounded-full bg-secondary-light" />
                                          <Skeleton className="size-4 rounded-full bg-secondary-light" />
                                        </div>
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            ))}
                          </div>
                        </main>
                      </div>
                    ) : isPurchasedPanel ? (
                      /* Purchased items: SettingSection > ScrollArea(px-4) > PTOPurchasedHistory (date groups + PTOPurchaseCard) */
                      <div className="w-full px-4 flex flex-col gap-4 flex-1 min-h-0">
                        <main className="flex flex-col w-full gap-4 min-h-full">
                          <div className="w-full flex flex-col">
                            {[1, 2].map((groupIdx) => (
                              <div key={groupIdx} className="w-full">
                                <div className="py-4">
                                  <Skeleton className="h-4 w-20 rounded-full bg-secondary-light" />
                                </div>
                                <div className="flex flex-col gap-4">
                                  {[1, 2].map((cardIdx) => (
                                    <div
                                      key={cardIdx}
                                      className="w-full flex flex-col items-start justify-between gap-4 border rounded-2xl p-4 bg-white border-gray-100"
                                    >
                                      <div className="w-full flex flex-row items-start justify-between gap-4">
                                        <div className="w-full flex flex-row items-start gap-4">
                                          {/* PTOTypePreview: media thumbnail */}
                                          <Skeleton className="size-16 rounded-xl shrink-0 bg-secondary-light" />
                                          <div className="w-full flex flex-1 flex-row gap-1 items-start justify-between min-w-0">
                                            <div className="w-full flex flex-col gap-1 items-start min-w-0">
                                              <Skeleton className="h-5 w-3/4 max-w-[200px] rounded-full bg-secondary-light" />
                                              <Skeleton className="h-4 w-20 rounded-full bg-secondary-light" />
                                            </div>
                                            <Skeleton className="size-5 rounded-full shrink-0 bg-secondary-light" />
                                          </div>
                                        </div>
                                      </div>
                                      <div className="flex flex-row items-center justify-between w-full gap-1">
                                        <Skeleton className="h-5 w-24 rounded-full bg-secondary-light" />
                                        <div className="flex items-center gap-2">
                                          <Skeleton className="size-6 rounded-full bg-secondary-light" />
                                          <Skeleton className="h-4 w-20 rounded-full bg-secondary-light" />
                                        </div>
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            ))}
                          </div>
                        </main>
                      </div>
                    ) : isListPanel ? (
                      <>
                        {withSearch && (
                          <div className="w-full mb-2 shrink-0">
                            <Skeleton className="h-10 w-full rounded-xl bg-secondary-light min-h-10" />
                          </div>
                        )}
                        <ActionListSkeleton />
                      </>
                    ) : (
                      /* Security / Deactivate style: 2 form rows */
                      <>
                        {[1, 2].map((i) => (
                          <div
                            key={i}
                            className="flex items-center justify-between gap-6 py-5 px-0 border-b border-gray-50 last:border-b-0"
                          >
                            <div className="flex-1 min-w-0">
                              <Skeleton className="h-5 w-3/4 max-w-xs rounded-md bg-secondary-light mb-1.5" />
                              <Skeleton className="h-5 w-1/2 max-w-[200px] rounded-md bg-secondary-light" />
                            </div>
                            <Skeleton className="h-6 w-11 rounded-full shrink-0 bg-secondary-light" />
                          </div>
                        ))}
                      </>
                    )}
                  </div>
                </div>
              </div>
            </main>
          </div>
        </div>
      </div>
    </div>
  );
}
